import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Text, Loader, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { GetWebBotBundleUrl, GetWebBotRuntimeUrl } from '@/APIURL';
import { StateGroupConfig, pickWeightedItem, WeightedActionItem } from './ManifestEditor/types';

let spineLoadingPromise: Promise<void> | null = null;

function loadSpineRuntime(): Promise<void> {
  if (typeof window !== 'undefined' && (window as any).spine?.SpinePlayer) {
    return Promise.resolve();
  }

  if (spineLoadingPromise) {
    return spineLoadingPromise;
  }

  spineLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-spine-player]') as HTMLScriptElement;
    if (existing) {
      if ((window as any).spine?.SpinePlayer) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (e) => {
          spineLoadingPromise = null;
          reject(e);
        });
      }
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = GetWebBotRuntimeUrl('spine/spine-player.css');
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.setAttribute('data-spine-player', 'true');
    script.src = GetWebBotRuntimeUrl('spine/spine-player.js');
    script.onload = () => resolve();
    script.onerror = (err) => {
      spineLoadingPromise = null;
      reject(err);
    };
    document.head.appendChild(script);
  });

  return spineLoadingPromise;
}

interface SpinePreviewProps {
  botProviderId: string;
  bundleName: string;
  skeletonFile: string | null;
  atlasFile: string | null;
  stateConfig?: StateGroupConfig;
  currentAnimation?: string;
  loop?: boolean;
  height?: number | string;
  replayTrigger?: number;
  onClick?: () => void;
  onComplete?: () => void;
  onAnimationsLoaded?: (animations: string[]) => void;
}

export function SpinePreview({
  botProviderId,
  bundleName,
  skeletonFile,
  atlasFile,
  stateConfig,
  currentAnimation = 'idle',
  loop = true,
  height = 200,
  replayTrigger = 0,
  onClick,
  onComplete,
  onAnimationsLoaded,
}: SpinePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const stateConfigRef = useRef(stateConfig);
  stateConfigRef.current = stateConfig;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const skeletonUrl = skeletonFile
    ? GetWebBotBundleUrl(botProviderId, `${bundleName}/${skeletonFile}`)
    : '';
  const atlasUrl = atlasFile
    ? GetWebBotBundleUrl(botProviderId, `${bundleName}/${atlasFile}`)
    : '';
  const isBinarySkel = !!skeletonFile?.endsWith('.skel');

  const playNextAction = useCallback((): WeightedActionItem | null => {
    if (!playerRef.current) return null;
    const cfg = stateConfigRef.current;
    if (cfg && cfg.items.length > 0) {
      const picked = pickWeightedItem(cfg.items);
      if (picked && picked.animation) {
        try {
          if (typeof playerRef.current.setAnimation === 'function') {
            playerRef.current.setAnimation(picked.animation, picked.loop !== false);
          }
        } catch (err) {
          console.warn(`Spine preview setAnimation "${picked.animation}" failed:`, err);
        }
        return picked;
      }
    } else if (currentAnimation) {
      try {
        if (typeof playerRef.current.setAnimation === 'function') {
          playerRef.current.setAnimation(currentAnimation, loop);
        }
      } catch (err) {
        console.warn(`Spine preview setAnimation "${currentAnimation}" failed:`, err);
      }
    }
    return null;
  }, [currentAnimation, loop]);

  useEffect(() => {
    if (!skeletonUrl || !atlasUrl) {
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch {
          // ignore
        }
        playerRef.current = null;
      }
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setErrorMsg(null);

    loadSpineRuntime()
      .then(() => {
        if (!isMounted || !containerRef.current) return;

        if (playerRef.current) {
          try {
            playerRef.current.dispose();
          } catch {
            // ignore
          }
          playerRef.current = null;
        }

        containerRef.current.innerHTML = '';

        const SpinePlayerClass = (window as any).spine?.SpinePlayer;
        if (!SpinePlayerClass) {
          throw new Error('SpinePlayer runtime 無法載入');
        }

        const pickedInitial = stateConfig?.items?.length
          ? pickWeightedItem(stateConfig.items)?.animation
          : null;
        const initialAnim = pickedInitial || currentAnimation || 'idle';

        const playerConfig: any = {
          atlasUrl,
          animation: initialAnim,
          premultipliedAlpha: true,
          backgroundColor: '#00000000',
          showControls: false,
          alpha: true,
          success: (p: any) => {
            if (isMounted) {
              playerRef.current = p;
              setIsLoading(false);

              // Complete listener for weighted re-rolling and action return in preview
              try {
                const animState = p.animationState;
                if (animState?.addListener) {
                  animState.addListener({
                    complete: () => {
                      if (!isMounted || !playerRef.current) return;
                      onCompleteRef.current?.();
                      const cfg = stateConfigRef.current;
                      if (cfg && cfg.strategy === 'on-complete' && cfg.items.length > 1) {
                        playNextAction();
                      }
                    },
                  });
                }
              } catch (listenerErr) {
                console.warn('Could not attach AnimationState listener in preview:', listenerErr);
              }

              const anims: string[] = p.skeleton?.data?.animations?.map((a: any) => a.name) || [];
              if (onAnimationsLoaded && anims.length > 0) {
                onAnimationsLoaded(anims);
              }
            }
          },
          error: (_p: any, msg: string) => {
            if (isMounted) {
              setIsLoading(false);
              setErrorMsg(msg || 'Spine 骨骼或圖集載入失敗');
            }
          },
        };

        if (isBinarySkel) {
          playerConfig.skelUrl = skeletonUrl;
        } else {
          playerConfig.jsonUrl = skeletonUrl;
        }

        new SpinePlayerClass(containerRef.current, playerConfig);
      })
      .catch((err) => {
        if (isMounted) {
          setIsLoading(false);
          setErrorMsg(err?.message || '載入 Spine Web Player 發生錯誤');
        }
      });

    return () => {
      isMounted = false;
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch {
          // ignore
        }
        playerRef.current = null;
      }
    };
  }, [skeletonUrl, atlasUrl, isBinarySkel]);

  useEffect(() => {
    if (!playerRef.current) return;
    playNextAction();
  }, [stateConfig, currentAnimation, loop, replayTrigger, playNextAction]);

  const handleContainerClick = () => {
    onClick?.();
  };

  if (!skeletonFile || !atlasFile) {
    return (
      <Box
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--mantine-color-gray-1)',
          borderRadius: 8,
        }}
      >
        <Text size="xs" c="dimmed">
          請先選取骨骼檔與圖集檔以預覽 Spine 動畫
        </Text>
      </Box>
    );
  }

  return (
    <Box
      onClick={handleContainerClick}
      style={{
        position: 'relative',
        width: '100%',
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--mantine-color-gray-1)',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      {isLoading && (
        <Box
          style={{
            position: 'absolute',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Loader size="sm" />
          <Text size="xs" c="dimmed">
            載入 Spine 動畫中...
          </Text>
        </Box>
      )}

      {errorMsg && (
        <Box p="xs" style={{ position: 'absolute', zIndex: 2, maxWidth: '90%' }}>
          <Alert icon={<IconAlertCircle size={16} />} title="Spine 載入錯誤" color="red" variant="light">
            <Text size="xs">{errorMsg}</Text>
          </Alert>
        </Box>
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </Box>
  );
}
